import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Adg4Button } from "@/components/adg4/button/src";
import { useSearchParameters } from "@/components/signup/hooks";
import { DataFoundationsWrapper } from "@/utils/data-foundations/data-foundation-types";

import { useScreenManager } from "../../hooks/use-screen-manager";
import { getPrimaryProduct } from "../../hooks/utils/use-cloud-status-utils";
import { useGetTeamTypeData } from "../../utils/utils";
import { JiraOnboardingContainer } from "../jira/jira-onboarding-container/jira-onboarding-container";

import { ProjectLandingSelectorButton } from "./components/selection-buttons";
import { SAMPLE_PROJECT_KEY, CREATE_PROJECT_KEY } from "./constants";
import { messages } from "./messages";

const buttonWrapperCss = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    margin: "0 auto",
    padding: "36px 20px 20px 20px",
    border: "none",
    gap: token("space.250"),
    width: "372px",
    [media.below.sm]: {
        position: "fixed",
        insetInlineStart: 0,
        insetBlockEnd: "0",
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#FFF",
        padding: "14px 18px",
        width: "100%",
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "-1px 0px 2px 0px #00000040"
    }
});

const gridWrapperCss = css({
    display: "grid",
    margin: "0 auto",
    border: "none",
    gap: "1.5rem",
    maxWidth: "850px",
    [media.below.sm]: {
        gridTemplateColumns: "none"
    }
});

const removeUrlParameters = (url: string, paramKeys: string[]) => {
    const r = new URL(url);
    paramKeys.forEach((key) => {
        r.searchParams.delete(key);
    });
    return r.href;
};

const selectionButtonData = [
    {
        key: SAMPLE_PROJECT_KEY,
        header: messages.exploreSampleProjectHeader,
        body: messages.exploreSampleProjectBody
    },
    {
        key: CREATE_PROJECT_KEY,
        header: messages.createProjectHeader,
        body: messages.createProjectBody
    }
];

const ProjectLandingSelectorView = () => {
    const { teamType } = useGetTeamTypeData();

    const { products, edition } = useSearchParameters();
    const productKey = getPrimaryProduct(products);
    const dataFoundationAttributes = {
        productKey,
        edition,
        teamType
    };

    const [selectedKey, setSelectedKey] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [randomized, setRandomized] = useState(false);

    const intl = useIntl();

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { prevScreen, nextScreen } = useScreenManager();

    const onSelection = (key: string, index: number) => {
        setSelectedKey(key);
        setSelectedIndex(index);
    };

    useEffect(() => {
        if (!randomized) {
            const r = Math.floor(Math.random() * 2);
            if (r === 0) {
                selectionButtonData.reverse();
            }
            setRandomized(true);
        }
    }, [randomized]);

    return (
        <DataFoundationsWrapper
            dataFoundationsEventOption="ProjectLandingSelectionScreenViewed"
            attributes={dataFoundationAttributes}
        >
            <JiraOnboardingContainer
                header={intl.formatMessage({
                    id: messages.header.id,
                    defaultMessage: messages.header.defaultMessage
                })}
                subheader={intl.formatMessage({
                    id: messages.subHeader.id,
                    defaultMessage: messages.subHeader.defaultMessage
                })}
                hideFooterOnMobile={true}
            >
                <div css={gridWrapperCss}>
                    {selectionButtonData.map((item, index) => {
                        return (
                            <ProjectLandingSelectorButton
                                key={item.key}
                                isChecked={selectedKey === item.key}
                                header={intl.formatMessage({
                                    id: item.header.id,
                                    defaultMessage: item.header.defaultMessage
                                })}
                                body={intl.formatMessage({
                                    id: item.body.id,
                                    defaultMessage: item.body.defaultMessage
                                })}
                                onClickFunc={() => {
                                    onSelection(item.key, index);
                                }}
                            />
                        );
                    })}
                </div>
                <div css={buttonWrapperCss}>
                    <Adg4Button
                        variant="secondary"
                        size="small"
                        color="black"
                        onClick={() => {
                            // Remove jtbd and template choices
                            const updatedUrl = removeUrlParameters(
                                window.location.href,
                                [
                                    "templateChoice",
                                    "jtbd",
                                    "jtbdTemplateOverride",
                                    "jtbdPersona"
                                ]
                            );
                            history.replaceState({}, "", updatedUrl);
                            prevScreen();
                        }}
                    >
                        {intl.formatMessage({
                            id: messages.backButton.id,
                            defaultMessage: messages.backButton.defaultMessage
                        })}
                    </Adg4Button>
                    <Adg4Button
                        variant="primary"
                        size="small"
                        color="blue"
                        isDisabled={selectedKey === ""}
                        onClick={() => {
                            createAnalyticsEvent({
                                actionSubject: "button",
                                action: "clicked",
                                actionSubjectId: "projectLandingSelection",
                                attributes: {
                                    selection: selectedKey,
                                    selectionIndex: selectedIndex
                                }
                            }).fire();
                            nextScreen();
                        }}
                    >
                        {intl.formatMessage({
                            id: messages.continueButton.id,
                            defaultMessage:
                                messages.continueButton.defaultMessage
                        })}
                    </Adg4Button>
                </div>
            </JiraOnboardingContainer>
        </DataFoundationsWrapper>
    );
};

export default ProjectLandingSelectorView;
